import React from "react";
import Skeleton from "../ui/Skeleton";

const SectionHeaderSkeleton: React.FC<{ hasAction?: boolean }> = ({
  hasAction = false,
}) => (
  <div className="flex items-start gap-3 border-b pb-2 border-purple-100 mb-3 shrink-0">
    <Skeleton
      variant="rectangular"
      width={40}
      height={40}
      className="rounded-xl shrink-0"
    />
    <div className="flex-1 min-w-0 h-10 flex flex-col justify-between">
      <Skeleton variant="text" width="35%" height="0.875rem" />
      <Skeleton variant="text" width="55%" height="0.75rem" />
    </div>
    {hasAction && (
      <Skeleton
        variant="rectangular"
        width={90}
        height={32}
        className="rounded-lg shrink-0"
      />
    )}
  </div>
);

export const DashboardSkeleton: React.FC = () => {
  return (
    <div className="w-full flex flex-col gap-2 animate-pulse">
      {/* ========================================================================= */}
      {/* BANDA 1: Header de Controle & 4 KPIs                                      */}
      {/* ========================================================================= */}
      <div className="shrink-0 flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
        <div className="flex items-start gap-3 border-b pb-2 border-purple-100 mb-3 shrink-0">
          <Skeleton
            variant="rectangular"
            width={40}
            height={40}
            className="rounded-xl shrink-0"
          />
          <div className="flex-1 min-w-0 h-10 flex flex-col justify-between">
            <Skeleton variant="text" width="30%" height="0.875rem" />
            <Skeleton variant="text" width="50%" height="0.75rem" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton
              variant="rectangular"
              width={100}
              height={28}
              className="rounded-lg hidden sm:block"
            />
            <Skeleton
              variant="rectangular"
              width={120}
              height={28}
              className="rounded-lg"
            />
          </div>
        </div>

        {/* Grid de 4 KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="relative bg-white rounded-xl border border-purple-200 p-3.5 shadow-xs flex flex-col justify-between min-h-[116px] overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-purple-100" />
              <div className="flex items-start justify-between pt-0.5 mb-2">
                <div className="flex flex-col min-w-0 flex-1 pr-2">
                  <Skeleton
                    variant="text"
                    width="60%"
                    height="0.75rem"
                    className="mb-1"
                  />
                  <Skeleton variant="text" width="45%" height="1.75rem" />
                </div>
                <Skeleton
                  variant="rectangular"
                  width={36}
                  height={36}
                  className="rounded-lg shrink-0"
                />
              </div>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={24}
                className="rounded-lg"
              />
            </div>
          ))}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* BANDA 2: Análise de Canais & Atividade Recente (Grid 2 Colunas)             */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-full">
        {/* Card: Posts por Plataforma */}
        <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeaderSkeleton />
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="p-2.5 rounded-xl border border-purple-100/60 flex flex-col gap-1.5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Skeleton
                      variant="rectangular"
                      width={28}
                      height={28}
                      className="rounded-lg"
                    />
                    <Skeleton variant="text" width={80} height="0.875rem" />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    width={36}
                    height={20}
                    className="rounded-sm"
                  />
                </div>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={4}
                  className="rounded-full"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Card: Atividade Recente */}
        <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeaderSkeleton hasAction={true} />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-2.5 rounded-xl border border-purple-100/60"
              >
                <div className="flex items-center gap-2.5 flex-1 pr-2">
                  <Skeleton
                    variant="circular"
                    width={8}
                    height={8}
                    className="shrink-0"
                  />
                  <Skeleton variant="text" width="70%" height="0.875rem" />
                </div>
                <Skeleton variant="text" width={50} height="0.75rem" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* BANDA 3: Ações Rápidas & Resumo do Dia (Grid 2 Colunas)                     */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-full">
        {/* Card: Ações Rápidas */}
        <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeaderSkeleton />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="p-3 rounded-xl border border-purple-100/80 flex items-center gap-2.5"
              >
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={32}
                  className="rounded-lg shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <Skeleton
                    variant="text"
                    width="70%"
                    height="0.875rem"
                    className="mb-1"
                  />
                  <Skeleton variant="text" width="90%" height="0.65rem" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Card: Resumo do Dia */}
        <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeaderSkeleton />
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-2.5 rounded-xl border border-purple-100/60"
              >
                <div className="flex items-center gap-2.5">
                  <Skeleton
                    variant="rectangular"
                    width={28}
                    height={28}
                    className="rounded-lg shrink-0"
                  />
                  <Skeleton variant="text" width={140} height="0.875rem" />
                </div>
                <Skeleton variant="text" width={30} height="1rem" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSkeleton;
